import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Words } from './motion/Reveal';

const DeliveredSection = () => {
  const { language } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/deliveries`);
        setVehicles(res.data || []);
      } catch {}
    };
    fetchData();
  }, []);

  const visibleCount = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 4;
  const maxIndex = Math.max(vehicles.length - visibleCount, 0);

  const next = useCallback(() => setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1), [maxIndex]);
  const prev = useCallback(() => setCurrentIndex(prev => prev <= 0 ? maxIndex : prev - 1), [maxIndex]);

  const touchStart = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, horizontal: null };
    setDragging(true);
  };
  const onTouchMove = (e) => {
    const s = touchStart.current;
    if (!s) return;
    const dx = e.touches[0].clientX - s.x;
    const dy = e.touches[0].clientY - s.y;
    if (s.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) s.horizontal = Math.abs(dx) > Math.abs(dy);
    if (s.horizontal) setDragX(dx);
  };
  const onTouchEnd = () => {
    if (dragX < -50) next();
    else if (dragX > 50) prev();
    touchStart.current = null;
    setDragX(0);
    setDragging(false);
  };

  useEffect(() => {
    if (vehicles.length === 0 || dragging) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [next, vehicles.length, dragging, currentIndex]);

  if (vehicles.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-[#f8faf7] overflow-hidden" data-testid="delivered-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <div>
            <p className="text-xs font-bold text-[#1a5c3b] uppercase tracking-[0.2em] mb-4">
              {language === 'en' ? 'Recently delivered' : 'Livrés récemment'}
            </p>
            <Words parts={[[language === 'en' ? 'Recent deliveries' : 'Nos livraisons récentes']]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a]" />
            <p className="text-lg text-slate-500 mt-2">
              {language === 'en'
                ? 'Real clients, real approvals, real vehicles.'
                : 'Vrais clients, vraies approbations, vrais véhicules.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={prev} className="w-10 h-10 rounded-full bg-white border border-slate-200 hover:border-[#1a5c3b] flex items-center justify-center text-slate-600 hover:text-[#1a5c3b] transition-all shadow-sm">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} className="w-10 h-10 rounded-full bg-white border border-slate-200 hover:border-[#1a5c3b] flex items-center justify-center text-slate-600 hover:text-[#1a5c3b] transition-all shadow-sm">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Vehicle Cards Carousel */}
        <div
          className="overflow-hidden touch-pan-y select-none"
          ref={trackRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
          data-testid="delivered-track"
        >
          <div
            className={`flex gap-5 ease-out ${dragging ? '' : 'transition-transform duration-500'}`}
            style={{ transform: `translateX(calc(-${currentIndex} * (${100 / visibleCount}% + ${visibleCount === 1 ? 20 : 5}px) + ${dragX}px))` }}
          >
            {vehicles.map((v, i) => (
              <div key={v.id || i} className="flex-shrink-0 w-full md:w-[calc(25%-15px)]">
                <div className="group block" data-testid={`delivered-card-${v.id}`}>
                  <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300">
                    <div className="relative h-48 md:h-52 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${v.image_url}`}
                        alt={language === 'en' ? 'Vehicle delivered by Credit Auto Reset' : 'Véhicule livré par Credit Auto Reset'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 bg-[#1a5c3b] text-white text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 shadow-lg">
                        <CheckCircle className="w-3 h-3" />
                        {language === 'en' ? 'DELIVERED' : 'LIVRÉ'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="flex items-center justify-between mt-10">
          <div className="flex items-center gap-3" data-testid="delivered-counter">
            <span className="text-sm font-bold text-[#1a5c3b] tabular-nums whitespace-nowrap">
              {Math.min(currentIndex + visibleCount, vehicles.length)} / {vehicles.length}
            </span>
            <div className="w-16 md:w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1a5c3b] rounded-full transition-[width] duration-500"
                style={{ width: `${(Math.min(currentIndex + visibleCount, vehicles.length) / vehicles.length) * 100}%` }}
              />
            </div>
          </div>
          <Link to="/demander-un-pret-auto" data-testid="delivered-cta">
            <Button className="bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold uppercase tracking-wide text-sm px-6 py-4 shadow-lg transition-all hover:scale-105">
              {language === 'en' ? 'Get yours too' : 'Obtenez le vôtre'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DeliveredSection;
